import React from 'react';
import { CheckCircle, Clock, XCircle, AlertCircle } from 'lucide-react';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';

const ApplicationTracker = ({ applications }) => {
  const getStatusIcon = (status) => {
    switch (status) {
      case 'approved':
        return <CheckCircle className="w-6 h-6 text-green-500" />;
      case 'rejected':
        return <XCircle className="w-6 h-6 text-red-500" />;
      case 'processing':
        return <Clock className="w-6 h-6 text-blue-500" />;
      case 'pending':
        return <AlertCircle className="w-6 h-6 text-yellow-500" />;
      default:
        return <Clock className="w-6 h-6 text-gray-400" />;
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'approved':
        return 'Genehmigt';
      case 'rejected':
        return 'Abgelehnt';
      case 'processing':
        return 'In Bearbeitung';
      case 'pending':
        return 'Ausstehend';
      default:
        return 'Unbekannt';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'rejected':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'processing':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getProgressSteps = (application) => {
    const steps = [
      { 
        label: 'Eingereicht', 
        completed: true, 
        date: application?.application_date 
      },
      { 
        label: 'In Bearbeitung', 
        completed: ['processing', 'approved', 'rejected']?.includes(application?.application_status),
        date: application?.application_status !== 'pending' ? application?.updated_at : null
      },
      { 
        label: application?.application_status === 'rejected' ? 'Abgelehnt' : 'Genehmigt', 
        completed: ['approved', 'rejected']?.includes(application?.application_status),
        date: application?.approval_date || (application?.application_status === 'rejected' ? application?.updated_at : null)
      },
      { 
        label: 'Abgeschlossen', 
        completed: application?.application_status === 'approved',
        date: application?.approval_date
      }
    ];
    return steps;
  };

  if (!applications || applications?.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-12 text-center">
        <AlertCircle className="w-16 h-16 text-gray-300 mx-auto mb-4" />
        <h3 className="text-xl font-semibold text-gray-900 mb-2">
          Keine Anträge vorhanden
        </h3>
        <p className="text-gray-600">
          Sie haben noch keine THG-Premium-Anträge eingereicht
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {applications?.map(application => {
        const steps = getProgressSteps(application);
        
        return (
          <div key={application?.id} className="bg-white rounded-lg shadow-sm overflow-hidden">
            {/* Application Header */}
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 p-6 text-white">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="text-xl font-bold mb-2">{application?.vehicle_model}</h3>
                  <p className="text-blue-100 text-sm">
                    Fahrgestellnummer: {application?.chassis_number || 'Nicht angegeben'}
                  </p>
                </div>
                <div className="flex items-center space-x-3">
                  {getStatusIcon(application?.application_status)}
                  <span className={`px-4 py-2 rounded-full text-sm font-semibold border ${getStatusColor(application?.application_status)}`}>
                    {getStatusLabel(application?.application_status)}
                  </span>
                </div>
              </div>
            </div>

            {/* Application Details */}
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                <div>
                  <p className="text-sm text-gray-500 mb-1">Antragsdatum</p>
                  <p className="font-semibold text-gray-900">
                    {application?.application_date 
                      ? format(new Date(application?.application_date), 'dd. MMMM yyyy', { locale: de })
                      : 'Nicht angegeben'
                    }
                  </p>
                </div>
                {application?.approval_date && (
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Genehmigungsdatum</p>
                    <p className="font-semibold text-green-600">
                      {format(new Date(application?.approval_date), 'dd. MMMM yyyy', { locale: de })}
                    </p>
                  </div>
                )}
                {application?.premium_amount && (
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Prämienbetrag</p>
                    <p className="font-semibold text-blue-600">
                      {Number(application?.premium_amount)?.toFixed(2)} €
                    </p>
                  </div>
                )}
                {application?.dealerships?.name && (
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Partner</p>
                    <p className="font-semibold text-gray-900">{application?.dealerships?.name}</p>
                  </div>
                )}
              </div>

              {/* Progress Tracker */}
              <div className="mb-6">
                <h4 className="text-sm font-semibold text-gray-700 mb-4">Fortschritt</h4>
                <div className="relative">
                  {/* Progress Line */}
                  <div className="absolute top-5 left-0 right-0 h-0.5 bg-gray-200">
                    <div 
                      className="h-full bg-blue-600 transition-all duration-500"
                      style={{ 
                        width: `${(steps?.filter(s => s?.completed)?.length / steps?.length) * 100}%` 
                      }}
                    />
                  </div>

                  {/* Progress Steps */}
                  <div className="relative flex justify-between">
                    {steps?.map((step, index) => (
                      <div key={index} className="flex flex-col items-center" style={{ width: `${100 / steps?.length}%` }}>
                        <div 
                          className={`w-10 h-10 rounded-full flex items-center justify-center border-2 z-10 transition-all ${
                            step?.completed 
                              ? 'bg-blue-600 border-blue-600' :'bg-white border-gray-300'
                          }`}
                        >
                          {step?.completed ? (
                            <CheckCircle className="w-6 h-6 text-white" />
                          ) : (
                            <div className="w-3 h-3 rounded-full bg-gray-300" />
                          )}
                        </div>
                        <p className={`mt-2 text-xs font-medium text-center ${
                          step?.completed ? 'text-blue-600' : 'text-gray-500'
                        }`}>
                          {step?.label}
                        </p>
                        {step?.date && (
                          <p className="text-xs text-gray-400 text-center mt-1">
                            {format(new Date(step?.date), 'dd.MM.yyyy', { locale: de })}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Rejection Reason */}
              {application?.application_status === 'rejected' && application?.rejection_reason && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <XCircle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-semibold text-red-800 mb-1">Ablehnungsgrund</p>
                      <p className="text-sm text-red-700">{application?.rejection_reason}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Notes */}
              {application?.notes && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-4">
                  <p className="font-semibold text-blue-800 mb-1">Hinweise</p>
                  <p className="text-sm text-blue-700">{application?.notes}</p>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default ApplicationTracker;